/**
 * Production composition — real HTTP cache client + real adapters.
 *
 * Weather, news, markets, and sports adapters are real; clock renders locally.
 * The cache client talks to the Worker at /api/tile; until the Worker deploy
 * lands, tiles show honest error chrome rather than fixture data — by design,
 * never fake data in prod.
 */
import type { CacheClient, ClientAdapter } from 'briefpane/contracts';
import { createHttpCache } from './http-client.js';
import { weatherAdapter } from '../adapters/weather/index.js';
import { newsAdapter } from '../adapters/news/index.js';
import { marketsAdapter } from '../adapters/markets/index.js';
import { sportsAdapter } from '../adapters/sports/index.js';
import { clockAdapter } from '../adapters/clock/index.js';
import { calendarAdapter } from '../adapters/calendar/index.js';
import { noteAdapter, todoAdapter, countdownAdapter } from '../adapters/local/index.js';
import { rssAdapter } from '../adapters/rss/index.js';

/** What the composition point resolves: one cache client + the adapter set. */
export interface Composition {
  readonly cache: CacheClient;
  readonly adapters: readonly ClientAdapter[];
}

/**
 * The Worker cache endpoint — absolute when built for the extension, relative
 * for the web build.
 *
 * Web build   (no VITE_API_BASE set):       '/api/tile'     — same-origin resolve ✓
 * Extension build (VITE_API_BASE set):      'https://briefpane.com/api/tile'      ✓
 *
 * The extension page runs on chrome-extension://<id>, so a relative path would
 * resolve to chrome-extension://<id>/api/tile (no such resource). VITE_API_BASE
 * is set to 'https://briefpane.com' in .env.extension (committed, no secrets).
 * The web build leaves VITE_API_BASE unset → empty string → relative path, same
 * as today. See extension/README.md and scripts/build-extension.mjs.
 */
export const CACHE_ENDPOINT_PATH =
  (import.meta.env.VITE_API_BASE ?? '') + '/api/tile';

/** Build the production wiring. Pure aside from constructing the client. */
export function makeProdComposition(): Composition {
  return {
    cache: createHttpCache(CACHE_ENDPOINT_PATH),
    adapters: [
      weatherAdapter as unknown as ClientAdapter,
      newsAdapter as unknown as ClientAdapter,
      marketsAdapter as unknown as ClientAdapter,
      sportsAdapter as unknown as ClientAdapter,
      clockAdapter as unknown as ClientAdapter,
      calendarAdapter as unknown as ClientAdapter,
      noteAdapter as unknown as ClientAdapter,
      todoAdapter as unknown as ClientAdapter,
      countdownAdapter as unknown as ClientAdapter,
      rssAdapter as unknown as ClientAdapter,
    ],
  };
}
